import React from 'react';
import { ButtonGroup } from '../../ButtonGroup';
import styles from '@danske/sapphire-css/components/pagination/pagination.module.css';
import { IconButton } from '../../IconButton';
import intlMessages from '../i18n';
import { useMessageFormatter } from '@react-aria/i18n';

export interface PageSelectionProps {
  currentPage: number;
  totalPageCount: number;
  siblingCount?: number;
  boundaryCount?: number;
}

export const PageSelectionRow = ({
  currentPage,
  totalPageCount,
  siblingCount,
  boundaryCount,
  onPagePress,
}: PageSelectionProps & {
  onPagePress?: (page: number) => void;
}): React.JSX.Element => {
  const slots = pageSlots({
    currentPage,
    totalPageCount,
    siblingCount,
    boundaryCount,
  });
  const formatMessage = useMessageFormatter(intlMessages);

  return (
    <ButtonGroup spacing="xs">
      {slots.map((slot, index) =>
        slot.type === 'ellipsis' ? (
          <div key={index} className={styles['sapphire-pagination__ellipsis']}>
            ...
          </div>
        ) : (
          <IconButton
            key={index}
            variant={slot.page === currentPage ? 'secondary' : 'tertiary'}
            size="md"
            onPress={() => onPagePress?.(slot.page)}
            zIndex={
              slot.page !== currentPage
                ? 1
                : undefined /* to avoid focus ring overlapping active page button */
            }
            aria-label={
              slot.page === currentPage
                ? formatMessage('page-number', { value: slot.page })
                : formatMessage('go-to-page', { value: slot.page })
            }
            {...(slot.page === currentPage ? { 'aria-current': true } : {})}
          >
            {slot.page}
          </IconButton>
        )
      )}
    </ButtonGroup>
  );
};

type PageSlot = { type: 'ellipsis' } | { type: 'page'; page: number };

export const pageSlots = (params: PageSelectionProps): Array<PageSlot> =>
  intersperse<Array<PageSlot>>(
    visiblePages(params).map((pageGroup) =>
      pageGroup.map((page) => ({ type: 'page', page }))
    ),
    [{ type: 'ellipsis' }]
  ).flatMap((pageSlot) => pageSlot);

const visiblePages = (params: PageSelectionProps): Array<Array<number>> => {
  const { currentPage, totalPageCount, siblingCount, boundaryCount } =
    normalizePageValues(params);

  /**
   * NOTE for maintainers.
   *
   * Below could be written as "boundaryCount * 2  + siblingCount * 2 + 3)"
   * but I wanted to better show what each number represents.
   *
   * Example where boundary is 1 and sibling is 1:
   *
   *    1       ...       5         6          7      ...      10
   * boundary ellipsis sibling selectedPage sibling ellipsis boundary
   */
  const maxPageSlots =
    boundaryCount +
    1 /* ellipsis */ +
    (siblingCount + 1 + siblingCount) /* visible middle range */ +
    1 /* ellipsis */ +
    boundaryCount;

  const pages = arrayOfNumbers({ from: 1, count: totalPageCount });

  if (pages.length <= maxPageSlots) {
    return [pages]; // eg: [[1, 2, 3, 4, 5, 6, 7, 8, 9, 10]]
  } else {
    const halfSlotsCount = Math.ceil(maxPageSlots / 2);
    const currentPageIndex = pages.findIndex((page) => page === currentPage);

    if (pages.slice(0, halfSlotsCount).includes(currentPage)) {
      return [
        pages.slice(0, halfSlotsCount + siblingCount),
        boundaryCount === 0 ? [] : pages.slice(-boundaryCount),
      ].slice(0, maxPageSlots); // eg: [[1, 2, 3, 4, 5], [10]]
    } else if (pages.slice(-halfSlotsCount).includes(currentPage)) {
      return [
        pages.slice(0, boundaryCount),
        pages.slice(-halfSlotsCount - siblingCount),
      ].slice(0, maxPageSlots); // eg: [[1], [6, 7, 8, 9, 10]]
    } else {
      return [
        pages.slice(0, boundaryCount),
        pages.slice(
          currentPageIndex - siblingCount,
          currentPageIndex + siblingCount + 1
        ),
        boundaryCount === 0 ? [] : pages.slice(-boundaryCount),
      ].slice(0, maxPageSlots); // eg: [[1], [4, 5, 6], [10]]
    }
  }
};

const normalizePageValues = ({
  currentPage,
  totalPageCount,
  siblingCount = 1,
  boundaryCount = 1,
}: PageSelectionProps): Required<PageSelectionProps> => ({
  totalPageCount: Math.max(totalPageCount, 1),
  currentPage: Math.min(Math.max(currentPage, 1), totalPageCount - 1),
  boundaryCount: Math.max(boundaryCount, 0),
  siblingCount: Math.max(siblingCount, 0),
});

const arrayOfNumbers = ({
  from,
  count,
}: {
  from: number;
  count: number;
}): Array<number> =>
  Array.from(new Array(count)).map((_, index) => from + index);

const intersperse = <T,>(array: T[], separator: T): T[] =>
  array.reduce((a: T[], v: T) => [...a, v, separator], []).slice(0, -1);
